import { useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plug } from "lucide-react";
import { toast } from "sonner";
import type { OAuthAuthorizationDetails } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  clientLabel,
  isAuthorizationId,
  isRetryableOAuthError,
  leaveTo,
  needsSignInAgain,
  uriHost,
} from "@/lib/oauth";

type ConsentState =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "unavailable" }
  | { kind: "consent"; details: OAuthAuthorizationDetails }
  | { kind: "redirecting" };

type Decision = "approve" | "deny";

/**
 * Consent page for the Supabase OAuth 2.1 server (ADR-058), at the
 * project's `authorization_url_path`. GoTrue sends the browser here with
 * `?authorization_id=` after a host (Claude, Claude Code, …) starts an
 * authorization-code flow.
 *
 * Mounted outside `ProtectedRoute`, which would send a session with a
 * pending workspace to `/complete-setup` before this could explain why
 * the request cannot be approved.
 *
 * The denial for a session without a workspace is a courtesy: the
 * boundary is the missing tenant claim, which leaves an OAuth-issued
 * token nothing to reach (KOP-96).
 */
export function OAuthConsent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rawAuthorizationId = searchParams.get("authorization_id");
  const authorizationId = isAuthorizationId(rawAuthorizationId)
    ? rawAuthorizationId
    : null;
  const { session, tenantId, loading: authLoading, signOut } = useAuth();

  const [state, setState] = useState<ConsentState>({ kind: "loading" });
  const [pending, setPending] = useState<Decision | null>(null);
  // Reading the details binds the authorization to this user, and when a
  // grant already exists it mints a code; do it once per user and id.
  const requested = useRef<string | null>(null);
  // Bumped by Retry after a load that failed for a retryable reason.
  const [attempt, setAttempt] = useState(0);

  const userId = session?.user.id ?? null;

  const leave = useCallback((url: string) => {
    setState(leaveTo(url) ? { kind: "redirecting" } : { kind: "invalid" });
  }, []);

  // The session was rejected: sign out so the effect sends the visitor to
  // login and back here, where the same user can pick the request up.
  const signInAgain = useCallback(async () => {
    requested.current = null;
    setPending(null);
    setState({ kind: "loading" });
    await signOut();
  }, [signOut]);

  useEffect(() => {
    if (authLoading) return;
    if (!authorizationId) {
      setState({ kind: "invalid" });
      return;
    }
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`,
        { replace: true },
      );
      return;
    }
    const key = `${userId}:${authorizationId}`;
    if (requested.current === key) return;
    requested.current = key;
    // Another user or request: nothing shown so far belongs to it.
    setState({ kind: "loading" });
    setPending(null);

    void (async () => {
      const { data, error } =
        await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      // A newer user or request took over while this one was in flight.
      if (requested.current !== key) return;
      if (error || !data) {
        if (error && needsSignInAgain(error)) {
          void signInAgain();
        } else if (error && isRetryableOAuthError(error)) {
          // Nothing was read, so the request may still be pending; let the
          // same key be asked again.
          requested.current = null;
          setState({ kind: "unavailable" });
        } else {
          setState({ kind: "invalid" });
        }
        return;
      }
      if ("redirect_url" in data) {
        // Already granted: GoTrue issued a code without asking again. This
        // also skips the no-workspace denial below, which is deliberate:
        // that denial is a courtesy, and a token issued to a user without
        // a tenant claim reaches nothing (KOP-96).
        leave(data.redirect_url);
        return;
      }
      setState({ kind: "consent", details: data });
    })();
  }, [
    authLoading,
    authorizationId,
    userId,
    navigate,
    location.pathname,
    location.search,
    attempt,
    leave,
    signInAgain,
  ]);

  const { data: workspaceName } = useQuery({
    queryKey: ["tenant-name", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants")
        .select("name")
        .eq("id", tenantId as string)
        .single();
      if (error) throw error;
      return (data as { name: string }).name;
    },
    enabled: state.kind === "consent" && tenantId !== null,
  });

  const decide = useCallback(
    async (decision: Decision) => {
      if (!authorizationId || pending) return;
      setPending(decision);
      const { data, error } =
        decision === "approve"
          ? await supabase.auth.oauth.approveAuthorization(authorizationId, {
              skipBrowserRedirect: true,
            })
          : await supabase.auth.oauth.denyAuthorization(authorizationId, {
              skipBrowserRedirect: true,
            });
      if (error || !data) {
        setPending(null);
        if (error && needsSignInAgain(error)) {
          void signInAgain();
        } else if (error && isRetryableOAuthError(error)) {
          toast.error("Something went wrong. Please try again.");
        } else {
          setState({ kind: "invalid" });
        }
        return;
      }
      leave(data.redirect_url);
    },
    [authorizationId, pending, leave, signInAgain],
  );

  const handleSignOut = async () => {
    // The request now belongs to this account, so signing in as someone
    // else cannot pick it up; they start again from their app.
    await signOut();
    navigate("/login", { replace: true });
  };

  if (state.kind === "loading" || state.kind === "redirecting") {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md" data-testid="oauth-consent-loading">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (state.kind === "unavailable") {
    return (
      <AuthLayout>
        <Card
          className="w-full max-w-md"
          data-testid="oauth-consent-unavailable"
        >
          <CardHeader>
            <CardTitle>Couldn&apos;t load this request</CardTitle>
            <CardDescription>
              Something went wrong while loading this authorization request.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              className="w-full"
              onClick={() => {
                setState({ kind: "loading" });
                setAttempt((n) => n + 1);
              }}
            >
              Try again
            </Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  if (state.kind === "invalid") {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md" data-testid="oauth-consent-invalid">
          <CardHeader>
            <CardTitle>This request can&apos;t be used</CardTitle>
            <CardDescription>
              This authorization request has expired or is invalid. Start again
              from your app.
            </CardDescription>
          </CardHeader>
        </Card>
      </AuthLayout>
    );
  }

  const { details } = state;
  const appName = clientLabel(details.client.name);
  const redirectHost =
    uriHost(details.redirect_uri) ?? clientLabel(details.redirect_uri);
  const pendingSetup = Boolean(session?.user.user_metadata?.pending_tenant_id);
  const busy = pending !== null;

  const signedInAs = (
    <p
      className="text-xs text-muted-foreground"
      data-testid="oauth-signed-in-as"
    >
      Signed in as <strong>{details.user.email}</strong>. Not you?{" "}
      <button
        type="button"
        onClick={handleSignOut}
        disabled={busy}
        className="underline underline-offset-2 hover:text-foreground"
      >
        Sign out
      </button>{" "}
      and start again from your app.
    </p>
  );

  const header = (
    <CardHeader>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted">
          <Plug className="h-5 w-5" aria-hidden="true" />
        </div>
        <CardTitle className="break-words" data-testid="oauth-client-name">
          {appName}
        </CardTitle>
      </div>
    </CardHeader>
  );

  const redirectLine = (
    <p className="text-sm">
      You&apos;ll be sent to{" "}
      <span
        className="font-mono font-semibold break-all"
        data-testid="oauth-redirect-host"
      >
        {redirectHost}
      </span>
      .
    </p>
  );

  if (!tenantId) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md" data-testid="oauth-consent-denial">
          {header}
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                {pendingSetup ? (
                  <>
                    Finish setting up your workspace first.{" "}
                    <Link to="/complete-setup" className="underline">
                      Complete setup
                    </Link>
                    , then start again from your app.
                  </>
                ) : (
                  <>
                    Your account doesn&apos;t have access to a workspace, so{" "}
                    {appName} can&apos;t be given access to any telemetry.
                  </>
                )}
              </AlertDescription>
            </Alert>
            {redirectLine}
            {signedInAs}
          </CardContent>
          <CardFooter>
            <Button
              className="w-full"
              variant="outline"
              disabled={busy}
              onClick={() => void decide("deny")}
            >
              {pending === "deny" && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Return to {redirectHost}
            </Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md" data-testid="oauth-consent">
        {header}
        <CardContent className="space-y-4">
          <p className="text-sm">
            <strong>{appName}</strong> is asking for read-only access to the
            telemetry of{" "}
            <strong data-testid="oauth-workspace">
              {workspaceName ?? "your workspace"}
            </strong>
            .
          </p>
          {redirectLine}
          <p className="text-xs text-muted-foreground">
            The app chose its own name. Approve only if you started this from an
            app you trust.
          </p>
          {signedInAs}
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button
            className="flex-1"
            variant="outline"
            disabled={busy}
            onClick={() => void decide("deny")}
          >
            {pending === "deny" && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Deny
          </Button>
          <Button
            className="flex-1"
            disabled={busy}
            onClick={() => void decide("approve")}
          >
            {pending === "approve" && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Approve
          </Button>
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
